import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import PubBadge from "../../components/PubBadge";
import { toast } from "sonner";
import { Star } from "lucide-react";

const STATUS_LABELS = {
  started: "Started",
  vendors_done: "Credits added",
  submitted: "Submitted",
  in_review: "In review",
  accepted: "Accepted",
  not_this_time: "Not this time",
  in_publishing: "In publishing",
  published: "Published",
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusFilter) params.set("status", statusFilter);
    const r = await api.get(`/admin/events?${params.toString()}`);
    setEvents(r.data.events);
  }, [q, statusFilter]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Event deleted");
    load();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="events-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="btw-overline mb-1">All submissions</p>
          <h1 className="font-serif text-3xl">Events</h1>
        </div>
        <div className="flex gap-3">
          <input
            placeholder="Search events…"
            data-testid="events-search"
            className="btw-input border border-[#dcd3c6] px-3 py-2 bg-white"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            data-testid="events-status-filter"
            className="btw-input border border-[#dcd3c6] px-3 py-2 bg-white appearance-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All status</option>
            {Object.entries(STATUS_LABELS).sort((a, b) => a[1].localeCompare(b[1])).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white border border-[#dcd3c6] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-[#5c5852] border-b border-[#dcd3c6]">
              <th className="text-left px-4 py-3">Event</th>
              <th className="text-left px-4 py-3">Publication</th>
              <th className="text-left px-4 py-3">Vendors</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-[#5c5852] italic">No events found.</td></tr>
            )}
            {events.map((e) => (
              <tr key={e.id} className="border-b border-[#dcd3c6]/50 hover:bg-[#e2d3c0]/20 transition-colors" data-testid={`event-row-${e.id}`}>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-2">
                    <Link to={`/admin/events/${e.id}`} className="font-medium hover:text-[#876a4f]">
                      {e.event_name}
                    </Link>
                    {e.priority && (
                      <Star size={14} className="text-[#876a4f] fill-[#c19872]" data-testid={`priority-${e.id}`} aria-label="Priority submission" />
                    )}
                    {e.open_to_either && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#c19872] text-[#876a4f] rounded-full" data-testid={`open-to-either-${e.id}`}>Open to either</span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3"><PubBadge pub={e.publication} /></td>
                <td className="px-4 py-3">{e.vendor_count}</td>
                <td className="px-4 py-3">
                  <span className={
                    ["accepted", "in_publishing", "published"].includes(e.status)
                      ? "bg-[#c19872]/20 text-[#876a4f] px-3 py-1 text-xs rounded-full"
                      : "bg-[#e2d3c0] text-[#5c5852] px-3 py-1 text-xs rounded-full"
                  } data-testid={`status-${e.id}`}>
                    {STATUS_LABELS[e.status] || e.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#5c5852]">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(e.id)} data-testid={`delete-${e.id}`} className="text-xs text-[#b35c5c] hover:underline">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
